import {Button, Col, Flex, Form, Input, Space, Table, Typography} from 'antd';
import {t} from 'i18next';
import {FC, useRef, useState} from 'react';
import {useParams, useSearchParams, useNavigate} from 'react-router-dom';
import {LogEvents, useSendEvent} from '@app/utils/logger';
import {
  useAddInventoryMedicineMutation,
  useGetInventoryQuery,
  useLazyGetInventoryQuery,
} from '@app/redux/services/inventoryService';
import ConfirmActionModal from '@app/components/Modal/ConfirmActionModal';
import {DeleteOutlined, EditOutlined, LeftOutlined} from '@ant-design/icons';
import useInventoryDetail from './useInventoryDetail';
import {TInventoryMedicine, TInventoryMedicineCreate} from '@app/types';
import InventoryDetailModal from '@app/components/Modal/InventoryDetailModal/InventoryDetailModal';
import {Routes} from '@app/router/Routes';
import Papa from 'papaparse';
import {CONSTANTS} from '@app/constants';

const {Text} = Typography;

const headerMap: Record<keyof TInventoryMedicineCreate, string> = {
  yj_code: '医薬品コード',
  name: '薬品名',
  unit: '単位',
  standard_capacity: '規格容量',
  piece_count_per_box: '箱内個数',
  box_count: '箱数',
  package_count: 'パッケージ数',
  piece_count_per_package: 'パッケージ内個数',
  piece_count: '個数',
  package_unit: '包装単位',
  expiration_date: '有効期限',
  shelf_number: '棚番号',
};

const reverseHeaderMap = Object.fromEntries(
  Object.entries(headerMap).map(([k, v]) => [v, k]),
) as Record<string, keyof TInventoryMedicineCreate>;

const InventoryDetail: FC = () => {
  const date = useParams().date ?? new Date().toISOString().slice(0, 10);
  const [searchParams, setSearchParams] = useSearchParams();
  const [addMedicine] = useAddInventoryMedicineMutation();
  const navigate = useNavigate();
  const sendEvent = useSendEvent();
  const [state, actions] = useInventoryDetail();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [searchText, setSearchText] = useState<string | undefined>(
    searchParams.get('search_keyword:like') ?? undefined,
  );
  const [fetchInventory] = useLazyGetInventoryQuery();

  const formattedDate = new Date(date).toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const resetPaginationParams = () => {
    searchParams.delete('page');
    searchParams.delete('limit');
  };

  const onSearch = () => {
    sendEvent(LogEvents.INVENTORY_LIST_SEARCH_PRESS, {text: searchText});
    if (searchText) searchParams.set('search_keyword:like', searchText);
    else searchParams.delete('search_keyword:like');
    resetPaginationParams();
    setSearchParams(searchParams);
  };

  const goBack = () => {
    sendEvent(LogEvents.INVENTORY_LIST_BACK_PRESS);
    navigate(Routes.InventoryList);
  };

  const columns = [
    {
      title: t('Medicine name'),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: '20%',
    },
    {
      title: t('Standard capacity'),
      dataIndex: 'standard_capacity',
      key: 'standard_capacity',
      sorter: true,
      width: '20%',
    },
    {
      title: t('Unit'),
      dataIndex: 'unit',
      key: 'unit',
      sorter: true,
      width: '10%',
    },
    {
      title: t('Stock quantity'),
      key: 'stock_quantity',
      sorter: true,
      width: '15%',
      render: (_: unknown, record: TInventoryMedicine) =>
        record.piece_count_per_box * record.box_count +
        record.package_count * record.piece_count_per_package +
        record.piece_count,
    },
    {
      title: t('Shelf number'),
      dataIndex: 'shelf_number',
      key: 'shelf_number',
      sorter: true,
      width: '15%',
    },
    {
      title: t('Operation'),
      dataIndex: 'operation',
      key: 'operation',
      sorter: false,
      width: '15%',
      render: (_: void, record: TInventoryMedicine) => (
        <Space>
          <Button
            onClick={() => actions.onDetailModalOpen(record.id, date)}
            icon={<EditOutlined />}
          >
            <Text className="btn-text">{t('Edit')}</Text>
          </Button>
          <Button
            onClick={() => actions.onDeleteModalOpen(record.id, date)}
            icon={<DeleteOutlined />}
            danger
          >
            <Text type="danger" className="btn-text">
              {t('Delete')}
            </Text>
          </Button>
        </Space>
      ),
    },
  ];

  const clickableColumns = columns.map((column) =>
    column.key !== 'operation'
      ? {
          ...column,
          onCell: (record: {id: string}) => ({
            onClick: () => {
              sendEvent(LogEvents.INVENTORY_DETAIL_EDIT_PRESS, {
                medicineId: record.id,
              });
              actions.onDetailModalOpen(record.id, date);
            },
          }),
        }
      : column,
  );

  const downloadCSV = async () => {
    const allInventoryData = await fetchInventory({
      date,
      limit: inventoryData?.medicines.meta.total_items,
      searchText: searchParams.get('search_keyword:like') ?? undefined,
    }).unwrap();

    const keys = Object.keys(headerMap) as (keyof TInventoryMedicineCreate)[];

    const escapeCSV = (value: string) => {
      const str = value ?? '';
      const escaped = str.replace(/"/g, '""');
      return /[",\n]/.test(str) ? `"${escaped}"` : escaped;
    };

    const headers = [...keys.map((k) => headerMap[k]), '合計'].join(',');

    const rows = allInventoryData.medicines.data.map((row) => {
      const mapped = row as Partial<TInventoryMedicineCreate>;
      const total =
        (Number(mapped.box_count) || 0) *
          (Number(mapped.piece_count_per_box) || 0) +
        (Number(mapped.package_count) || 0) *
          (Number(mapped.piece_count_per_package) || 0) +
        (Number(mapped.piece_count) || 0);

      const cells = keys.map((key) => escapeCSV(String(row[key] ?? '')));
      cells.push(String(total));
      return cells.join(',');
    });

    const csvContent = [headers, ...rows].join('\r\n');
    const BOM = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([BOM, csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `棚卸し リスト ${formattedDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportCSV = (file: File) => {
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target?.result as string;
      const parsed = Papa.parse<Record<string, string>>(text, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.trim(),
      });

      const records = parsed.data;
      if (!records.length) {
        console.error('CSV parse error: no records found');
        return;
      }

      const data: TInventoryMedicineCreate[] = records.map(
        (row) =>
          Object.fromEntries(
            Object.entries(reverseHeaderMap).map(([jp, key]) => [
              key,
              [
                'piece_count_per_box',
                'box_count',
                'package_count',
                'piece_count_per_package',
                'piece_count',
              ].includes(key)
                ? Number(row[jp])
                : row[jp],
            ]),
          ) as TInventoryMedicineCreate,
      );

      try {
        await addMedicine({date, body: data}).unwrap();
      } catch (e) {
        console.error('Failed to export CSV:', e);
      }
    };
    reader.readAsText(file);
  };

  const {data: inventoryData, isLoading} = useGetInventoryQuery({
    date,
    limit: searchParams.get('limit')
      ? Number(searchParams.get('limit'))
      : CONSTANTS.DEFAULT_PAGINATION_LIMIT,
    page: searchParams.get('page')
      ? Number(searchParams.get('page'))
      : undefined,
    searchText: searchParams.get('search_keyword:like') ?? undefined,
  });

  return (
    <div>
      <InventoryDetailModal
        medicineId={state.selectedMedicineId ?? ''}
        date={date}
        open={state.isDetailModalOpen ?? false}
        onCancel={actions.onDetailModalClose}
      />
      <Flex justify="center" style={{position: 'relative', marginBottom: 20}}>
        <Button
          color="primary"
          variant="solid"
          onClick={goBack}
          style={{position: 'absolute', left: 0}}
        >
          <LeftOutlined />
          {t('Go Back')}
        </Button>
        <Typography.Title level={3} style={{margin: 0}}>
          {formattedDate}
        </Typography.Title>
      </Flex>
      <Flex style={{position: 'relative'}}>
        <Col span={24} md={8} style={{marginBottom: 4}}>
          <Form.Item>
            <Input.Search
              onSearch={onSearch}
              onChange={(e) => setSearchText(e.target.value)}
              value={searchText}
              placeholder={t('Search')}
              enterButton
            />
          </Form.Item>
        </Col>
        <div style={{right: 0, position: 'absolute'}}>
          <Button
            color="primary"
            variant="solid"
            style={{marginRight: 10}}
            onClick={() => fileInputRef.current?.click()}
          >
            {t('CSV Input')}
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv"
            style={{display: 'none'}}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) exportCSV(file);
            }}
          />
          <Button color="primary" variant="solid" onClick={downloadCSV}>
            {t('CSV Output')}
          </Button>
        </div>
      </Flex>

      <Table
        columns={clickableColumns}
        dataSource={inventoryData?.medicines.data}
        pagination={{
          total: inventoryData?.medicines.meta.total_items,
          pageSize: inventoryData?.medicines.meta.items_per_page,
          current: Number(searchParams.get('page') ?? '1'),
          position: ['bottomRight'],
          hideOnSinglePage: false,
        }}
        sticky={false}
        loading={isLoading}
        rowKey={(item) => item.id}
        onChange={actions.onTableChange}
        locale={{
          emptyText: (
            <div style={{textAlign: 'left'}}>
              {t('There is currently no inventory available for display.')}
            </div>
          ),
        }}
      />

      <ConfirmActionModal
        open={state.isDeleteModalOpen}
        onConfirm={actions.onDeleteConfirm}
        onCancel={actions.onDeleteModalClose}
        text={t('Is it okay to delete this medicine?')}
      />
    </div>
  );
};

export default InventoryDetail;
